/**
 * Date helpers for sending datetimes to the API.
 *
 * The API/DB stores everything as UTC. `Date.prototype.toISOString()` (which is what
 * `JSON.stringify`/HttpClient use under the hood for a `Date` object) always renders the
 * instant in UTC. That part is fine.
 *
 * The bug to avoid: Angular Material's datepicker builds a `Date` at LOCAL midnight for the
 * day the user picked (e.g. picking "July 14" produces `2026-07-14T00:00:00` in the browser's
 * local time zone). Calling `.toISOString()` on that local-midnight `Date` converts it to the
 * corresponding UTC instant, which for any timezone behind UTC rolls the calendar date back to
 * the previous day (e.g. `2026-07-13T05:00:00.000Z` for UTC-5). So a "same instant" conversion
 * silently corrupts calendar dates.
 *
 * `toUtcIsoDate` fixes this by re-building the Date from its LOCAL year/month/day (and, for
 * timestamp fields, local hours/minutes/seconds) as UTC components, so the calendar
 * date/time the user selected is preserved exactly once it lands in UTC.
 */

/**
 * Converts a "date only" value (e.g. from a datepicker) to an ISO-8601 UTC string
 * representing midnight UTC on the selected calendar date, regardless of the browser's
 * local time zone.
 */
export function toUtcIsoDate(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
}

/**
 * Converts a date/time value to an ISO-8601 UTC string, preserving the local
 * year/month/day/hour/minute/second the user selected (rather than reinterpreting the
 * local wall-clock time as if it were already UTC).
 */
export function toUtcIsoDateTime(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds()
  )).toISOString();
}
